import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlarmClock, ArrowUpRight, ClipboardCheck, PartyPopper, ScanLine, Sparkles, UserPlus, Users } from "lucide-react";
import { CaptureLeadDialog } from "@/components/CaptureLeadDialog";
import { api, stageOf, timeAgo } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHead, Panel } from "@/pages/vendor/Shell";

const Metric = ({ icon: Icon, label, value, accent, testid }: any) => (
  <Panel className="rise" data-testid={testid}>
    <div className="flex items-center justify-between text-muted-foreground">
      <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      <Icon className="h-4 w-4" style={{ color: accent }} />
    </div>
    <p className="mt-3 font-mono text-3xl font-semibold">{value}</p>
  </Panel>
);

const LeadList = ({ title, items, icon: Icon, testid, empty }: any) => (
  <Panel data-testid={testid}>
    <div className="mb-3 flex items-center justify-between">
      <h3 className="flex items-center gap-2 font-semibold"><Icon className="h-4 w-4 text-emerald-600" /> {title}</h3>
      <Link to="/dashboard/leads" className="text-xs text-muted-foreground hover:text-emerald-600">View all</Link>
    </div>
    {items.length === 0 && <p className="py-4 text-sm text-muted-foreground">{empty}</p>}
    <div className="divide-y divide-border">
      {items.map((l: any) => (
        <Link key={l.id} to={`/dashboard/leads?lead=${l.id}`} className="flex items-center gap-3 py-2.5 hover:text-emerald-600" data-testid={`${testid}-item-${l.id}`}>
          <span className="h-2 w-2 rounded-full" style={{ background: stageOf(l.stage).color }} />
          <span className="flex-1 truncate text-sm font-medium">{l.name}</span>
          <span className="hidden truncate font-mono text-[11px] text-muted-foreground sm:block">{l.event_name}</span>
          <span className="text-xs text-muted-foreground">{timeAgo(l.created_at)}</span>
        </Link>
      ))}
    </div>
  </Panel>
);

export default function Overview() {
  const { vendor } = useAuth();
  const [d, setD] = useState<any>(null);
  const [capturing, setCapturing] = useState(false);
  useEffect(() => {
    api.get("/vendor/overview").then((r) => setD(r.data));
  }, []);
  if (!d) return <p className="text-muted-foreground">Loading...</p>;
  const m = d.metrics;
  return (
    <div>
      <PageHead eyebrow={vendor.vendor.name} title="Today at the booth">
        <button onClick={() => setCapturing(true)} className="hard-shadow inline-flex items-center gap-1.5 rounded-sm bg-stone-900 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-800" data-testid="overview-capture-lead-button">
          <UserPlus className="h-4 w-4" /> Capture lead
        </button>
        <Link to="/dashboard/events" className="hard-shadow inline-flex items-center gap-1 rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90" data-testid="overview-booth-links-button">
          Booth QR codes <ArrowUpRight className="h-4 w-4" />
        </Link>
      </PageHead>
      {capturing && <CaptureLeadDialog onClose={() => setCapturing(false)} />}
      {m.pending_followup > 0 && (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-50 px-4 py-3 text-sm text-amber-800 rise" data-testid="overview-pending-banner">
          <AlarmClock className="h-4 w-4" /> {m.pending_followup} lead{m.pending_followup > 1 ? "s" : ""} waiting for follow-up{d.unread ? ` · ${d.unread} unread chat` : ""}.
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric icon={Sparkles} label="New leads" value={m.new} accent="#3B82F6" testid="metric-new-leads" />
        <Metric icon={AlarmClock} label="Pending follow-up" value={m.pending_followup} accent="#F59E0B" testid="metric-pending" />
        <Metric icon={ClipboardCheck} label="Quote requests" value={m.quote_requests} accent="#06B6D4" testid="metric-quotes" />
        <Metric icon={PartyPopper} label="Booked / won" value={m.won} accent="#E05A33" testid="metric-won" />
      </div>
      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Panel className="xl:col-span-2">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">Scans vs leads · 14 days</h3>
            <div className="flex gap-4 font-mono text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ScanLine className="h-3.5 w-3.5" /> {m.scans} scans</span>
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {m.leads} leads</span>
              <span data-testid="metric-conversion">{m.conversion}% won</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.series}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E05A33" stopOpacity={0.35} /><stop offset="100%" stopColor="#E05A33" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#EEEEEE" vertical={false} />
                <XAxis dataKey="date" stroke="#737373" fontSize={11} tickLine={false} />
                <YAxis stroke="#737373" fontSize={11} tickLine={false} allowDecimals={false} width={28} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E5E5", borderRadius: 4 }} />
                <Area type="monotone" dataKey="scans" stroke="#A3A3A3" fill="transparent" strokeDasharray="4 4" />
                <Area type="monotone" dataKey="leads" stroke="#E05A33" fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <LeadList title="Newest leads" items={d.recent} icon={Sparkles} testid="overview-recent" empty="No leads yet — print your booth QR code." />
        <LeadList title="Pending follow-up" items={d.pending} icon={AlarmClock} testid="overview-pending" empty="All caught up." />
        <LeadList title="Quote requests" items={d.quotes} icon={ClipboardCheck} testid="overview-quotes" empty="No open quote requests." />
      </div>
    </div>
  );
}
